import { useEffect, useId, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { physicalClientsApi } from "@/lib/api";
import { CPF_FORBIDDEN_MESSAGE, isForbidden } from "@/lib/physicalClientError";
import { CPF_REVEAL_MS, createRevealTimer } from "./revealTimer";

interface ViewCpfButtonProps {
  physicalClientId: number;
  /** ***.456.789-** */
  cpfMasked: string;
  /** Query do registro de acessos, atualizada depois de cada visualização. */
  accessLogQueryKey: readonly unknown[];
}

/**
 * CPF mascarado com a ação "Ver CPF". O CPF completo vem de POST /physical-clients/{id}/document (que a API registra:
 * quem, quando e o motivo) e fica só no estado deste componente por 30 segundos ou até sair da tela. Não passa pelo
 * TanStack Query (nem query nem mutation: os dois guardam valores em cache) e nunca vai para toast, título ou console.
 * Todo usuário do backoffice é administrador, que é a regra da API para ver o CPF completo; um 403 vira aviso.
 */
export function ViewCpfButton({ physicalClientId, cpfMasked, accessLogQueryKey }: ViewCpfButtonProps) {
  const reasonId = useId();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [cpf, setCpf] = useState<string | null>(null);
  const timer = useMemo(() => createRevealTimer(() => setCpf(null), CPF_REVEAL_MS), []);

  // Ao sair da tela (ou trocar de cliente), o CPF some.
  useEffect(() => {
    return () => timer.stop();
  }, [timer]);
  useEffect(() => {
    setCpf(null);
    timer.stop();
  }, [physicalClientId, timer]);

  const hide = () => {
    timer.stop();
    setCpf(null);
  };

  const reveal = async () => {
    setLoading(true);
    try {
      const document = await physicalClientsApi.revealDocument(physicalClientId, reason.trim() || undefined);
      setCpf(document.cpf);
      timer.start();
      setDialogOpen(false);
      setReason("");
      void queryClient.invalidateQueries({ queryKey: accessLogQueryKey });
    } catch (error) {
      toast.error(isForbidden(error) ? CPF_FORBIDDEN_MESSAGE : "Não foi possível mostrar o CPF.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-mono" aria-live="polite">
        {cpf ?? cpfMasked}
      </span>
      {cpf ? (
        <>
          <span className="text-xs text-muted-foreground">visível por {CPF_REVEAL_MS / 1000} segundos</span>
          <Button variant="ghost" size="sm" onClick={hide}>
            <EyeOff className="mr-1 h-4 w-4" aria-hidden="true" />
            Ocultar CPF
          </Button>
        </>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
          <Eye className="mr-1 h-4 w-4" aria-hidden="true" />
          Ver CPF
        </Button>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ver CPF completo</DialogTitle>
            <DialogDescription>
              O acesso fica registrado (quem e quando) e aparece na aba "Acessos ao CPF". O CPF é exibido por{" "}
              {CPF_REVEAL_MS / 1000} segundos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor={reasonId}>Motivo (opcional)</Label>
            <Textarea
              id={reasonId}
              value={reason}
              maxLength={200}
              rows={2}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={() => void reveal()} disabled={loading}>
              {loading ? "Carregando..." : "Mostrar CPF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
